import type { ActionType, SequenceRepeatToken, SequenceToken } from "../types/action";
import type { SymbolDictionaryData } from "../types/symbol";

export interface SymbolParserResult {
  readonly raw: readonly SequenceToken[];
  readonly expanded: readonly ActionType[];
  readonly valid: boolean;
  readonly reason: string | null;
}

const REPEAT_COUNT_BY_TOKEN: Readonly<Record<SequenceRepeatToken, number>> = {
  "3": 3,
  "5": 5,
};

function isRepeatToken(token: SequenceToken): token is SequenceRepeatToken {
  return token === "3" || token === "5";
}

function createActionLookup(dictionary: SymbolDictionaryData): ReadonlyMap<string, ActionType> {
  const entries = dictionary.symbols.map((symbol) => [symbol.token, symbol.action] as const);
  return new Map<string, ActionType>(entries);
}

export function parseSequence(
  dictionary: SymbolDictionaryData,
  tokens: readonly SequenceToken[],
): SymbolParserResult {
  const raw = [...tokens];
  const actionLookup = createActionLookup(dictionary);
  const expanded: ActionType[] = [];

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (token === undefined) {
      break;
    }

    if (isRepeatToken(token)) {
      const repeatedToken = tokens[index + 1];

      if (repeatedToken === undefined) {
        return {
          raw,
          expanded: [],
          valid: false,
          reason: `Repeat token \"${token}\" must be followed by a symbol token.`,
        };
      }

      if (isRepeatToken(repeatedToken)) {
        return {
          raw,
          expanded: [],
          valid: false,
          reason: `Repeat token \"${token}\" cannot target another repeat token \"${repeatedToken}\".`,
        };
      }

      const action = actionLookup.get(repeatedToken);
      if (action === undefined) {
        return {
          raw,
          expanded: [],
          valid: false,
          reason: `Unknown symbol token: ${repeatedToken}`,
        };
      }

      expanded.push(...Array.from({ length: REPEAT_COUNT_BY_TOKEN[token] }, () => action));
      index += 1;
      continue;
    }

    // 未知 token 不参与展开（expanded），但保留在 raw 中供 HUD 显示。
    // 用于 Level 0 等需要非符号指示文本（如 "→"）的场景。
    const action = actionLookup.get(token);
    if (action === undefined) {
      continue;
    }

    expanded.push(action);
  }

  return {
    raw,
    expanded,
    valid: true,
    reason: null,
  };
}
